/**
 * SiluxPromise — reusable USP / "promise" band for SEO & GEO landing pages.
 *
 * Surfaces Silux Mobile's core commitments for both human visitors AND
 * generative engines. The same claims are mirrored in each page's FAQ
 * (FAQPage JSON-LD) so they're picked up by Google AI Overviews / SGE / LLMs.
 *
 * Key claims: no price increases (no price rises during contracts),
 * free upgrades / free broadband upgrades, no credit checks on SIM contracts,
 * and no hidden fees.
 *
 * variant:
 *   'sim'       — SIM-focused (includes "no credit checks on SIM contracts")
 *   'broadband' — broadband-focused (includes "free broadband upgrades")
 *   'all'       — full mixed set (homepage / multi-product pages)
 */
import { ShieldCheck, Lock, Gift, CreditCard, XCircle, TrendingDown } from 'lucide-react'

type Variant = 'sim' | 'broadband' | 'all'

interface Promise {
  icon: typeof ShieldCheck
  title: string
  sub?: string
}

const PROMISES: Record<Variant, Promise[]> = {
  sim: [
    { icon: Lock, title: 'No price increases', sub: 'Your price is locked — no price rises during your contract, ever' },
    { icon: Gift, title: 'Free upgrades', sub: 'Upgrade your plan or SIM for free, any time' },
    { icon: CreditCard, title: 'No credit checks', sub: 'No credit checks on our SIM contracts' },
    { icon: XCircle, title: 'No hidden fees', sub: 'No hidden fees, no price increases' },
  ],
  broadband: [
    { icon: Lock, title: 'No price increases', sub: 'Fixed price — no price increases during your contract' },
    { icon: Gift, title: 'Free broadband upgrades', sub: 'Free upgrades to faster plans and kit' },
    { icon: TrendingDown, title: 'No mid-contract rises', sub: 'No annual or inflation-linked price hikes' },
    { icon: XCircle, title: 'No hidden fees', sub: 'No setup fees, no engineer — router delivered to your door' },
  ],
  all: [
    { icon: Lock, title: 'No price increases', sub: 'Price locked — no price rises during your contract' },
    { icon: Gift, title: 'Free upgrades', sub: 'Free plan and free broadband upgrades, any time' },
    { icon: CreditCard, title: 'No credit checks', sub: 'No credit checks on our SIM contracts' },
    { icon: XCircle, title: 'No hidden fees', sub: 'The price displayed is the price you pay, incl. VAT' },
  ],
}

export default function SiluxPromise({
  variant = 'all',
  className = '',
}: {
  variant?: Variant
  className?: string
}) {
  const items = PROMISES[variant]
  return (
    <section
      className={`py-12 px-4 bg-white border-y border-gray-100 ${className}`}
      aria-label="The Silux Mobile promise"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-center gap-2 mb-8">
          <ShieldCheck className="w-5 h-5 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900 text-center">The Silux Mobile Promise</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map(item => (
            <div
              key={item.title}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-center"
            >
              <item.icon className="w-8 h-8 text-primary-600 mx-auto mb-3" />
              <p className="font-bold text-gray-900 text-sm">{item.title}</p>
              {item.sub && <p className="text-xs text-gray-500 mt-1 leading-relaxed">{item.sub}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
